import { formatAprValue } from "rujira.ui";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { fetchQuery, useRelayEnvironment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  assetLabel,
  Button,
  Card,
  formatApr,
  IconDenom,
  Icons,
  useTranslation,
} from "rujira.ui";
import { homeAutomatedOpportunityQuery } from "../../analytics/HomeAutomatedOpportunity";
import { HomeAutomatedOpportunityQuery } from "../../analytics/__generated__/HomeAutomatedOpportunityQuery.graphql";
import {
  MIN_AGE_DAYS,
  MIN_VALUE_USD_SCALED,
} from "../../algorithmic/constants";
import { Link } from "../../Gate";
import {
  getAnalyticsEnvironment,
  isAnalyticsConfigured,
} from "../../services/relayAnalytics";
import { EarningOpportunitiesQuery } from "./__generated__/EarningOpportunitiesQuery.graphql";

// APR sorting currently fails at runtime with these typename filters, so use a
// bounded TVL-ranked candidate set and compare APRs locally.
const query = graphql`
  query EarningOpportunitiesQuery {
    strategies(
      first: 100
      typenames: ["GhostVault", "StakingPool"]
      sortBy: TVL
      sortDir: DESC
    ) {
      edges {
        node {
          __typename
          ... on GhostVault {
            id
            asset {
              asset
              type
              chain
              metadata {
                symbol
              }
            }
            status {
              apr {
                status
                value
              }
            }
          }
          ... on StakingPool {
            id
            bondAsset {
              metadata {
                symbol
              }
            }
            stakingSummary: summary {
              apr {
                status
                value
              }
            }
          }
        }
      }
    }
  }
`;

const ROTATION_INTERVAL_MS = 6000;
const HOME_BLUE_CHIP_SYMBOLS = new Set(["BTC", "ETH", "USDC", "USDT"]);
const AUTOMATED_CACHE_KEY = "rujira-home-automated-opportunity-v1";
const AUTOMATED_CACHE_TTL_MS = 60 * 60 * 1000;

type Opportunity = {
  apr: string;
  aprPrefix?: string;
  assetKind: string;
  assetName: ReactNode;
  cta: string;
  denoms: string[];
  description: string;
  id: "automated" | "lending" | "staking";
  title: string;
  to: string;
};

type StrategyEdge = NonNullable<
  NonNullable<
    EarningOpportunitiesQuery["response"]["strategies"]["edges"]
  >[number]
>;
type StrategyNode = NonNullable<StrategyEdge["node"]>;
type LendingNode = Extract<StrategyNode, { readonly __typename: "GhostVault" }>;
type StakingNode = Extract<
  StrategyNode,
  { readonly __typename: "StakingPool" }
>;
type AutomatedRanges = NonNullable<
  HomeAutomatedOpportunityQuery["response"]["finV3"]["ranges"]
>;
type AutomatedEdge = NonNullable<NonNullable<AutomatedRanges["edges"]>[number]>;
type AutomatedNode = NonNullable<AutomatedEdge["node"]>;
type AutomatedOpportunity = {
  apr: bigint;
  assetBase: AutomatedNode["pair"]["assetBase"];
  assetQuote: AutomatedNode["pair"]["assetQuote"];
};
type AutomatedCacheRecord = {
  fetchedAt: number;
  opportunity: null | (Omit<AutomatedOpportunity, "apr"> & { apr: string });
};

const findAutomatedOpportunity = (
  data: HomeAutomatedOpportunityQuery["response"]
): AutomatedOpportunity | null => {
  const node = data.finV3.ranges?.edges?.find((edge) => {
    const pair = edge?.node?.pair;
    const baseSymbol = pair?.assetBase.metadata?.symbol;
    const quoteSymbol = pair?.assetQuote.metadata?.symbol;

    return (
      !!baseSymbol &&
      !!quoteSymbol &&
      HOME_BLUE_CHIP_SYMBOLS.has(baseSymbol) &&
      HOME_BLUE_CHIP_SYMBOLS.has(quoteSymbol)
    );
  })?.node;

  return node?.analytics?.apr == null
    ? null
    : {
        apr: node.analytics.apr,
        assetBase: node.pair.assetBase,
        assetQuote: node.pair.assetQuote,
      };
};

const readAutomatedCache = () => {
  try {
    const raw = localStorage.getItem(AUTOMATED_CACHE_KEY);
    if (!raw) return { hit: false as const, opportunity: null };

    const record: AutomatedCacheRecord = JSON.parse(raw);
    if (Date.now() - record.fetchedAt >= AUTOMATED_CACHE_TTL_MS)
      return { hit: false as const, opportunity: null };

    return {
      hit: true as const,
      opportunity: record.opportunity
        ? { ...record.opportunity, apr: BigInt(record.opportunity.apr) }
        : null,
    };
  } catch {
    return { hit: false as const, opportunity: null };
  }
};

const writeAutomatedCache = (opportunity: AutomatedOpportunity | null) => {
  try {
    const record: AutomatedCacheRecord = {
      fetchedAt: Date.now(),
      opportunity: opportunity
        ? { ...opportunity, apr: opportunity.apr.toString() }
        : null,
    };
    localStorage.setItem(AUTOMATED_CACHE_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable in private or locked-down browser contexts.
  }
};

const isBetterOpportunity = (
  candidateApr: bigint | null | undefined,
  currentApr: bigint | null | undefined,
  candidateLabel: string,
  currentLabel: string
) => {
  if (candidateApr != null && currentApr == null) return true;
  if (candidateApr == null && currentApr != null) return false;
  if (candidateApr != null && currentApr != null && candidateApr !== currentApr)
    return candidateApr > currentApr;

  return candidateLabel.localeCompare(currentLabel) < 0;
};

const stakingDisplay = (symbol: string | null | undefined) =>
  symbol === "LP-X/RUJI-RUNE"
    ? { denoms: ["RUJI", "RUNE"], label: "RUJI / RUNE CLP" }
    : { denoms: symbol ? [symbol] : [], label: symbol || "" };

export const EarningOpportunities: FC = () => {
  const environment = useRelayEnvironment();
  const reducedMotion = useReducedMotion();
  const { t } = useTranslation();
  const [coreData, setCoreData] =
    useState<EarningOpportunitiesQuery["response"]>();
  const [automatedOpportunity, setAutomatedOpportunity] =
    useState<AutomatedOpportunity>();
  const [coreReady, setCoreReady] = useState(false);
  const [automatedReady, setAutomatedReady] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const paginationPointerFocus = useRef(false);

  useEffect(() => {
    const coreSubscription = fetchQuery<EarningOpportunitiesQuery>(
      environment,
      query,
      {},
      { fetchPolicy: "store-or-network" }
    ).subscribe({
      next: (data) => {
        setCoreData(data);
        setCoreReady(true);
      },
      error: (error: Error) => {
        console.error("[Home] Earning opportunities failed", error);
        setCoreReady(true);
      },
      complete: () => setCoreReady(true),
    });
    const cached = readAutomatedCache();
    if (cached.hit) {
      setAutomatedOpportunity(cached.opportunity ?? undefined);
      setAutomatedReady(true);
      return () => coreSubscription.unsubscribe();
    }

    if (!isAnalyticsConfigured()) {
      setAutomatedReady(true);
      return () => coreSubscription.unsubscribe();
    }

    const automatedSubscription = fetchQuery<HomeAutomatedOpportunityQuery>(
      getAnalyticsEnvironment(),
      homeAutomatedOpportunityQuery,
      {
        minInvestmentAgeDays: MIN_AGE_DAYS,
        // Relay JSON-stringifies variables for its request identifier. This
        // value is an exact safe integer, while the generated scalar maps all
        // Bigint inputs to the bigint TypeScript type.
        minValueUsd: MIN_VALUE_USD_SCALED as unknown as bigint,
      },
      { fetchPolicy: "store-or-network" }
    ).subscribe({
      next: (data) => {
        const opportunity = findAutomatedOpportunity(data);
        setAutomatedOpportunity(opportunity ?? undefined);
        writeAutomatedCache(opportunity);
        setAutomatedReady(true);
      },
      error: (error: Error) => {
        console.error("[Home] Automated opportunity failed", error);
        setAutomatedReady(true);
      },
      complete: () => setAutomatedReady(true),
    });

    return () => {
      coreSubscription.unsubscribe();
      automatedSubscription.unsubscribe();
    };
  }, [environment]);

  const opportunities = useMemo<Opportunity[]>(() => {
    const result: Opportunity[] = [];
    let lending: LendingNode | undefined;
    let staking: StakingNode | undefined;

    for (const edge of coreData?.strategies.edges ?? []) {
      const node = edge?.node;

      if (node?.__typename === "GhostVault") {
        const symbol = node.asset.metadata?.symbol;
        if (
          symbol &&
          HOME_BLUE_CHIP_SYMBOLS.has(symbol) &&
          (!lending ||
            isBetterOpportunity(
              node.status.apr.value,
              lending.status.apr.value,
              symbol,
              lending.asset.metadata?.symbol ?? ""
            ))
        )
          lending = node;
      }

      if (node?.__typename === "StakingPool") {
        const symbol = node.bondAsset.metadata?.symbol;
        if (
          symbol &&
          (!staking ||
            isBetterOpportunity(
              node.stakingSummary.apr.value,
              staking.stakingSummary.apr.value,
              symbol,
              staking.bondAsset.metadata?.symbol ?? ""
            ))
        )
          staking = node;
      }
    }

    if (automatedOpportunity) {
      const { apr, assetBase, assetQuote } = automatedOpportunity;
      result.push({
        apr: formatAprValue(apr),
        aprPrefix: t("earningUpTo"),
        assetKind: t("earningPair"),
        assetName: (
          <>
            {assetLabel(assetBase)}{" "}
            <span className="color-grey">{assetLabel(assetQuote)}</span>
          </>
        ),
        cta: t("earningCta"),
        denoms: [assetBase.metadata.symbol, assetQuote.metadata.symbol],
        description: t("earningAutomatedDescription"),
        id: "automated",
        title: t("earningAutomatedTitle"),
        to: "/automated-trading",
      });
    }

    if (lending) {
      const symbol = lending.asset.metadata.symbol || "";
      result.push({
        apr: formatApr(lending.status.apr),
        assetKind: t("earningToken"),
        assetName: assetLabel(lending.asset),
        cta: t("earningCta"),
        denoms: [symbol],
        description: t("earningLendingDescription"),
        id: "lending",
        title: t("earningLendingTitle"),
        to: "/lend",
      });
    }

    if (staking) {
      const { denoms, label } = stakingDisplay(
        staking.bondAsset.metadata.symbol
      );
      result.push({
        apr: formatApr(staking.stakingSummary.apr),
        assetKind: t("earningToken"),
        assetName: label,
        cta: t("earningCta"),
        denoms,
        description: t("earningStakingDescription"),
        id: "staking",
        title: t("earningStakingTitle"),
        to: "/stake",
      });
    }

    return result;
  }, [automatedOpportunity, coreData, t]);

  const loading = !coreReady && !automatedReady;
  const paused = hovered || focusWithin || !!reducedMotion;

  useEffect(() => {
    setActiveIndex(0);
  }, [opportunities.length]);

  useEffect(() => {
    if (paused || opportunities.length < 2) return;

    const timeout = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % opportunities.length);
    }, ROTATION_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [activeIndex, opportunities.length, paused]);

  const activeOpportunity = opportunities[activeIndex] ?? opportunities[0];

  if (loading || !activeOpportunity) return null;

  return (
    <section
      className="home__earning"
      aria-labelledby="earning-opportunities-title">
      <h2 id="earning-opportunities-title" className="visually-hidden">
        {t("earningOpportunitiesTitle")}
      </h2>
      <div
        className="flex jc-c home__earning-interaction"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={(event) => {
          setHovered(false);

          if (!paginationPointerFocus.current) return;
          paginationPointerFocus.current = false;

          const focusedElement = document.activeElement;
          if (
            focusedElement instanceof HTMLElement &&
            event.currentTarget.contains(focusedElement)
          )
            focusedElement.blur();
        }}
        onKeyDownCapture={() => {
          paginationPointerFocus.current = false;
        }}
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setFocusWithin(false);
        }}>
        <Card className={`home__earning-card${paused ? " is-paused" : ""}`}>
          <div
            className="home__earning-stage"
            aria-live={paused ? "polite" : "off"}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={activeOpportunity.id}
                className="home__earning-content"
                initial={reducedMotion ? false : { opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -16 }}
                transition={{
                  duration: reducedMotion ? 0.01 : 0.34,
                  ease: [0.22, 1, 0.36, 1],
                }}>
                <div className="home__earning-header">
                  <div>
                    <h3>{activeOpportunity.title}</h3>
                    <p className="balance">{activeOpportunity.description}</p>
                  </div>
                </div>

                <div className="home__earning-controls">
                  <div
                    className="home__earning-pagination"
                    role="group"
                    aria-label={t("earningOpportunitySelector")}>
                    {opportunities.map((opportunity, index) => (
                      <button
                        key={opportunity.id}
                        type="button"
                        className={index === activeIndex ? "is-active" : ""}
                        aria-label={t("earningShowOpportunity", {
                          opportunity: opportunity.title,
                        })}
                        aria-pressed={index === activeIndex}
                        onPointerDown={() => {
                          paginationPointerFocus.current = true;
                        }}
                        onClick={() => setActiveIndex(index)}>
                        <span aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="home__earning-market">
                  <div className="home__earning-asset">
                    <span className="home__earning-label">
                      {activeOpportunity.assetKind}
                    </span>
                    <span className="home__earning-asset-value">
                      <span className="home__earning-denoms">
                        {activeOpportunity.denoms.map((denom, index) => (
                          <IconDenom
                            key={`${denom}-${index}`}
                            denom={denom}
                            className="home__earning-denom"
                          />
                        ))}
                      </span>
                      <span>{activeOpportunity.assetName}</span>
                    </span>
                  </div>
                  <div className="home__earning-apr">
                    <span className="home__earning-label">
                      {t("earningApr")} {activeOpportunity.aprPrefix}
                    </span>
                    <span className="home__earning-apr-value">
                      <strong>{activeOpportunity.apr}</strong>
                    </span>
                  </div>
                </div>

                <Button
                  as={Link}
                  to={activeOpportunity.to}
                  className="button--small button--icon-right button--blue home__earning-cta"
                  label={activeOpportunity.cta}>
                  <Icons.AngleRight aria-hidden="true" />
                </Button>
              </motion.div>
            </AnimatePresence>
          </div>
        </Card>
      </div>
    </section>
  );
};
